import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, collectionGroup, updateDoc, doc, getDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { useUnit } from '@/contexts/UnitContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import WelcomeModal from '@/components/WelcomeModal';
import { RSVP, EventInvitation, Invoice, Announcement, Event, UnitType, EventStatus } from '@/types';
import { formatDate } from '@/lib/utils';
import { INVOICE_AMOUNT_EPS } from '@/lib/invoice-utils';
import { mapEventData, mapRsvpData, isActiveRsvpData, isInviteeCoveredByInviter } from '@/lib/event-utils';
import { getHeadlinePayableDueForDashboard } from '@/lib/invoice-payment-breakdown';
import { getPersonalDashboardRsvpStatus, isRsvpEffectivelyPaidForDashboard } from '@/lib/personal-dashboard-rsvp-status';
import { normaliseDateValue } from '@/lib/date-normalise';
import { unitDisplaysNumber } from '@/lib/unit-number-display';
import { Calendar, Plus, UserPlus, Users, Ticket, Mail, CheckCircle, XCircle, MapPin, DollarSign, Bell, TrendingUp, Building2, Eye, CreditCard, Clock, UserCircle, X } from 'lucide-react';
import { showSuccess, showError } from '@/lib/swal';

// Unified event item type
type UnifiedEventItem = {
  id: string;
  type: 'upcoming' | 'past' | 'invitation' | 'payment' | 'pending_rsvp' | 'waitlist';
  eventName: string;
  eventDate: Date | null;
  unitName?: string;
  lodgeName: string;
  status: string;
  statusVariant: 'default' | 'secondary' | 'destructive' | 'warning' | 'outline';
  amountDue?: number;
  isPaid?: boolean;
  unitId?: string;
  lodgeId?: string;
  eventId?: string;
  rsvpId?: string;
  invoiceId?: string;
  invitationData?: EventInvitation;
  /** True when this row is an RSVP and the inviter pays for this guest's seat (personal dashboard privacy). */
  hostPays?: boolean;
  originalData: any;
};

type UnitWithStats = {
  unit: any;
  memberCount: number;
  eventCount: number;
  nextEventDate: Date | null;
  userRole: string;
  entityName: string;
};

interface PendingUnitApplication {
  id: string;
  name: string;
  unitNumber: string;
  typeId?: string;
  submittedAt: Date;
  reviewStatus: 'pending' | 'denied';
  denialReason?: string;
  messageCount?: number;
}

/** Announcements on the personal dashboard should only apply to upcoming events the user is actually on the list for. */
type DashboardAnnouncementRow = Announcement & {
  eventName?: string;
  unitId?: string;
  eventId?: string;
  lodgeId?: string;
  eventDate?: Date;
  eventStatus?: EventStatus;
  eventDocExists?: boolean;
};

function isEventFinishedForAnnouncements(eventDate: Date | undefined, eventStatus: EventStatus | undefined): boolean {
  if (eventStatus === 'completed' || eventStatus === 'closed') {
    return true;
  }
  if (eventDate && eventDate.getTime() <= Date.now()) {
    return true;
  }
  return false;
}

function userHasEligibleRsvpForAnnouncement(
  unitId: string,
  eventId: string,
  myRsvps: Array<RSVP & { unitId?: string; lodgeId?: string }>
): boolean {
  return myRsvps.some((r) => {
    if (r.deletedAt != null) {
      return false;
    }
    if (r.eventId !== eventId) {
      return false;
    }
    const rUnit = r.unitId || r.lodgeId;
    if (rUnit !== unitId) {
      return false;
    }
    if (r.apologies === true || r.status === 'declined' || r.status === 'cancelled') {
      return false;
    }
    return r.status === 'confirmed' || r.status === 'waitlist';
  });
}

export default function PersonalDashboardPage() {
  const { userProfile, currentUser, refreshUserProfile } = useAuth();
  const { units, setCurrentUnit, loading: unitsLoading } = useUnit();
  const [myRsvps, setMyRsvps] = useState<Array<RSVP & { eventName?: string; eventDate?: Date; unitId?: string; unitName?: string; lodgeId?: string; lodgeName?: string; eventStatus?: EventStatus }>>([]);
  
  // Helper to check if user is admin for detailed logging
  const isAdmin = userProfile?.isAdmin === true;
  
  // Helper function to format data for debugging
  const formatAdminData = (data: any): any => {
    if (data === null || data === undefined) {
      return data;
    }
    
    // Handle arrays
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return '[] (empty array)';
      }
      // If array of objects, create a table-like summary
      if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
        const keys = Object.keys(data[0]);
        return {
          _type: 'array',
          _length: data.length,
          _summary: `Array of ${data.length} objects with keys: ${keys.join(', ')}`,
          _data: data.map((item, idx) => ({
            _index: idx,
            ...formatAdminData(item)
          })),
          // Also provide as JSON for easy inspection
          _json: JSON.stringify(data, null, 2)
        };
      }
      // Simple array - show first few items and length
      return {
        _type: 'array',
        _length: data.length,
        _preview: data.slice(0, 5),
        _hasMore: data.length > 5,
        _json: JSON.stringify(data, null, 2)
      };
    }
    
    // Handle Maps
    if (data instanceof Map) {
      const entries = Array.from(data.entries());
      return {
        _type: 'Map',
        _size: data.size,
        _entries: entries.map(([key, value]) => ({
          key,
          value: formatAdminData(value)
        })),
        _json: JSON.stringify(Object.fromEntries(data), null, 2)
      };
    }
    
    // Handle Sets
    if (data instanceof Set) {
      return {
        _type: 'Set',
        _size: data.size,
        _values: Array.from(data).map(formatAdminData),
        _json: JSON.stringify(Array.from(data), null, 2)
      };
    }
    
    // Handle objects
    if (typeof data === 'object') {
      // Handle Firestore Timestamps
      if (data && typeof data.toDate === 'function') {
        return {
          _type: 'FirestoreTimestamp',
          _value: data.toDate().toISOString(),
          _raw: data
        };
      }
      
      // Regular objects - format recursively
      const formatted: any = {};
      for (const [key, value] of Object.entries(data)) {
        formatted[key] = formatAdminData(value);
      }
      return {
        _type: 'object',
        _keys: Object.keys(data),
        _data: formatted,
        _json: JSON.stringify(data, (key, val) => {
          // Handle Firestore Timestamps in JSON
          if (val && typeof val.toDate === 'function') {
            return val.toDate().toISOString();
          }
          return val;
        }, 2)
      };
    }
    
    // Primitive values
    return data;
  };
  
  // Admin-only detailed logging helper with formatted output
  const adminLog = (...args: any[]) => {
    if (isAdmin) {
      const formattedArgs = args.map(arg => {
        // If it's a string label followed by data, format the data
        if (args.length > 1 && typeof args[0] === 'string' && args.indexOf(arg) > 0) {
          return formatAdminData(arg);
        }
        // Format all non-string primitives
        if (typeof arg !== 'string' && typeof arg !== 'number' && typeof arg !== 'boolean') {
          return formatAdminData(arg);
        }
        return arg;
      });
      console.log('[ADMIN DEBUG]', ...formattedArgs);
      // Also log as table if it's an array of objects
      if (args.length > 0) {
        const lastArg = args[args.length - 1];
        if (Array.isArray(lastArg) && lastArg.length > 0 && typeof lastArg[0] === 'object') {
          console.table(lastArg.slice(0, 50)); // Limit to 50 rows for performance
        }
      }
    }
  };
  
  const adminWarn = (...args: any[]) => {
    if (isAdmin) {
      const formattedArgs = args.map(arg => {
        if (typeof arg !== 'string' && typeof arg !== 'number' && typeof arg !== 'boolean') {
          return formatAdminData(arg);
        }
        return arg;
      });
      console.warn('[ADMIN DEBUG]', ...formattedArgs);
    }
  };
  
  const adminError = (...args: any[]) => {
    if (isAdmin) {
      const formattedArgs = args.map(arg => {
        if (typeof arg !== 'string' && typeof arg !== 'number' && typeof arg !== 'boolean') {
          return formatAdminData(arg);
        }
        return arg;
      });
      console.error('[ADMIN DEBUG]', ...formattedArgs);
    }
  };
  
  // Helper to log arrays as tables
  const adminLogTable = (label: string, data: any[]) => {
    if (isAdmin && Array.isArray(data)) {
      console.group(`[ADMIN DEBUG] ${label} (${data.length} items)`);
      if (data.length > 0) {
        // Log summary
        console.log('Summary:', {
          count: data.length,
          keys: data[0] && typeof data[0] === 'object' ? Object.keys(data[0]) : 'N/A'
        });
        // Log as table (limit to 50 for performance)
        if (data.length <= 50) {
          console.table(data);
        } else {
          console.table(data.slice(0, 50));
          console.log(`... and ${data.length - 50} more items (showing first 50)`);
        }
        // Also provide JSON for full data
        console.log('Full data (JSON):', JSON.stringify(data, (key, val) => {
          if (val && typeof val.toDate === 'function') {
            return val.toDate().toISOString();
          }
          return val;
        }, 2));
      } else {
        console.log('Array is empty');
      }
      console.groupEnd();
    }
  };
  const [eventInvitations, setEventInvitations] = useState<EventInvitation[]>([]);
  const [myInvoices, setMyInvoices] = useState<Array<Invoice & { eventName?: string }>>([]);
  const [rsvpInvoices, setRsvpInvoices] = useState<Map<string, Invoice>>(new Map());
  /** Event + RSVPs per invoice id; same shape as PaymentPage breakdown for aligned balances. */
  const [invoiceBreakdowns, setInvoiceBreakdowns] = useState<
    Map<string, { event: Event; rsvps: RSVP[] }>
  >(new Map());
  const [unreadAnnouncementCandidates, setUnreadAnnouncementCandidates] = useState<DashboardAnnouncementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [rsvpsLoading, setRsvpsLoading] = useState(true);
  const [unitsStatsLoading, setUnitsStatsLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [unitsWithStats, setUnitsWithStats] = useState<UnitWithStats[]>([]);
  const [pendingUnits, setPendingUnits] = useState<PendingUnitApplication[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [rsvpFilter, setRsvpFilter] = useState<'ALL' | 'ACTIVE' | 'FINISHED'>('ACTIVE');
  const fetchedInvoiceIdsRef = useRef<Set<string>>(new Set());
  const navigate = useNavigate();

  const visibleUnreadAnnouncements = useMemo(() => {
    return unreadAnnouncementCandidates.filter((a) => {
      if (!a.eventDocExists) {
        return false;
      }
      if (isEventFinishedForAnnouncements(a.eventDate, a.eventStatus)) {
        return false;
      }
      const unitId = a.unitId || a.lodgeId;
      if (!unitId || !a.eventId) {
        return false;
      }
      return userHasEligibleRsvpForAnnouncement(unitId, a.eventId, myRsvps);
    });
  }, [unreadAnnouncementCandidates, myRsvps]);

  // Fetch unit types
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setUnitTypes(data.unitTypes || []);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  // Check if user should see welcome modal
  useEffect(() => {
    if (userProfile && !userProfile.hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, [userProfile]);

  const handleCloseWelcome = async () => {
    setShowWelcome(false);
    
    // Mark as seen in Firestore
    if (currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          hasSeenWelcome: true,
        });
        await refreshUserProfile();
      } catch (error) {
        console.error('Error updating hasSeenWelcome:', error);
      }
    }
  };

  // Fetch pending unit applications
  useEffect(() => {
    const fetchPendingUnits = async () => {
      if (!currentUser) return;

      try {
        // Fetch units created by this user that are pending or denied
        const pendingQuery = query(
          collection(db, 'units'),
          where('createdBy', '==', currentUser.uid),
          where('reviewStatus', 'in', ['pending', 'denied'])
        );

        const snapshot = await getDocs(pendingQuery);
        
        const applications: PendingUnitApplication[] = await Promise.all(
          snapshot.docs.map(async (unitDoc) => {
            const data = unitDoc.data();
            
            // Count messages for this unit
            let messageCount = 0;
            try {
              const messagesQuery = query(
                collection(db, 'unitApplicationMessages'),
                where('unitId', '==', unitDoc.id)
              );
              const messagesSnapshot = await getDocs(messagesQuery);
              messageCount = messagesSnapshot.size;
            } catch (error) {
              console.error('Error fetching message count:', error);
            }

            return {
              id: unitDoc.id,
              name: data.name,
              unitNumber: data.unitNumber || data.lodgeNumber || data.number || '',
              typeId: data.typeId,
              submittedAt: data.reviewData?.submittedAt?.toDate() || data.createdAt?.toDate() || new Date(),
              reviewStatus: data.reviewStatus,
              denialReason: data.reviewData?.denialReason,
              messageCount,
            };
          })
        );

        // Filter out dismissed applications
        const dismissedIds = userProfile?.dismissedUnitApplications || userProfile?.dismissedLodgeApplications || [];
        const visibleApplications = applications.filter(app => !dismissedIds.includes(app.id));
        
        setPendingUnits(visibleApplications);
      } catch (error) {
        console.error('Error fetching pending unit applications:', error);
      }
    };

    fetchPendingUnits();
  }, [currentUser, userProfile]);

  // Fetch user's RSVPs
  useEffect(() => {
    const fetchMyRsvps = async () => {
      if (!currentUser) {
        setRsvpsLoading(false);
        return;
      }

      if (unitsLoading) {
        // Wait for units to load before fetching RSVPs
        // (needed for root-level RSVPs that require searching through units)
        return;
      }

      const emailCandidates = new Set<string>();
      const addEmailCandidate = (email?: string | null) => {
        if (!email) return;
        const trimmed = email.trim();
        if (!trimmed) return;
        emailCandidates.add(trimmed.toLowerCase());
      };

      addEmailCandidate(currentUser.email);
      addEmailCandidate(userProfile?.email);
      addEmailCandidate(userProfile?.pendingEmailChange?.newEmail);

      adminLog('=== RSVP FETCH START ===');
      adminLog('User ID:', currentUser.uid);
      adminLog('User Email:', currentUser.email);
      adminLog('User Profile:', userProfile);
      adminLog('Is Admin:', isAdmin);
      adminLog('Email candidates:', Array.from(emailCandidates));
      adminLog('Units loading:', unitsLoading);
      adminLogTable('Units available', units.map(u => ({ id: u.id, name: u.name, unitNumber: u.unitNumber || u.lodgeNumber })));
      
      console.log('Fetching RSVPs for user:', currentUser.uid, 'email candidates:', Array.from(emailCandidates));

      try {
        const rsvpDocsMap = new Map();
        
        // Query by userId
        try {
          console.log('Querying RSVPs by userId:', currentUser.uid);
          const rsvpsByUserIdQuery = query(
            collectionGroup(db, 'rsvps'),
            where('userId', '==', currentUser.uid)
          );
          const rsvpsByUserIdSnapshot = await getDocs(rsvpsByUserIdQuery);
          console.log('Found RSVPs by userId:', rsvpsByUserIdSnapshot.size);
          adminLog('RSVPs by userId query result:', rsvpsByUserIdSnapshot.size);
          
          rsvpsByUserIdSnapshot.docs.forEach(doc => {
            const data = doc.data();
            adminLog('RSVP by userId - ID:', doc.id, 'Path:', doc.ref.path, 'Data:', {
              userId: data.userId,
              email: data.email,
              status: data.status,
              eventId: data.eventId,
              eventName: data.eventName,
              eventDate: data.eventDate,
              unitId: data.unitId,
              unitName: data.unitName,
              paid: data.paid,
              apologies: data.apologies,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            });
            console.log('RSVP by userId:', doc.id, doc.data());
            rsvpDocsMap.set(doc.id, doc);
          });
        } catch (error) {
          console.error('Error querying RSVPs by userId:', error);
          // Continue with email query even if this fails
        }

        // Query by email (support multiple email candidates in case of historical changes)
        for (const emailCandidate of emailCandidates) {
          try {
            console.log('Querying RSVPs by email:', emailCandidate);
            const rsvpsByEmailQuery = query(
              collectionGroup(db, 'rsvps'),
              where('email', '==', emailCandidate)
            );
            const rsvpsByEmailSnapshot = await getDocs(rsvpsByEmailQuery);
            console.log('Found RSVPs by email candidate:', emailCandidate, rsvpsByEmailSnapshot.size);
            adminLog('RSVPs by email query result:', emailCandidate, 'count:', rsvpsByEmailSnapshot.size);
            
            rsvpsByEmailSnapshot.docs.forEach(doc => {
              const data = doc.data();
              adminLog('RSVP by email - Email:', emailCandidate, 'ID:', doc.id, 'Path:', doc.ref.path, 'Data:', {
                userId: data.userId,
                email: data.email,
                status: data.status,
                eventId: data.eventId,
                eventName: data.eventName,
                eventDate: data.eventDate,
                unitId: data.unitId,
                unitName: data.unitName,
                paid: data.paid,
                apologies: data.apologies,
                createdAt: data.createdAt,
                updatedAt: data.updatedAt,
              });
              console.log('RSVP by email candidate:', emailCandidate, doc.id, doc.data());
              rsvpDocsMap.set(doc.id, doc);
            });
          } catch (error) {
            console.error('Error querying RSVPs by email:', emailCandidate, error);
            // Continue even if this query fails for a specific candidate
          }
        }

        console.log('Total unique RSVPs found:', rsvpDocsMap.size);
        adminLog('Total unique RSVPs in map:', rsvpDocsMap.size);
        adminLog('RSVP document IDs:', Array.from(rsvpDocsMap.keys()));

        const fetchedRsvps = await Promise.all(
          Array.from(rsvpDocsMap.values()).map(async (rsvpDoc) => {
            const rsvpData = rsvpDoc.data();
            if (!isActiveRsvpData(rsvpData)) {
              adminLog('Skipping soft-deleted RSVP:', rsvpDoc.id);
              return null;
            }
            const pathParts = rsvpDoc.ref.path.split('/');
            
            adminLog('--- Processing RSVP ---');
            adminLog('RSVP ID:', rsvpDoc.id);
            adminLog('RSVP Path:', rsvpDoc.ref.path);
            adminLog('Path Parts:', pathParts);
            adminLog('Raw RSVP Data:', JSON.parse(JSON.stringify(rsvpData)));
            console.log('Processing RSVP:', rsvpDoc.id, 'Path:', rsvpDoc.ref.path, 'Path parts:', pathParts);
            
            let unitId: string | undefined;
            let eventId: string | undefined;
            
            // Determine if this is a subcollection path (units/{unitId}/events/{eventId}/rsvps/{rsvpId})
            // or a root-level path (rsvps/{rsvpId})
            if (pathParts.length >= 6 && pathParts[0] === 'units' && pathParts[2] === 'events' && pathParts[4] === 'rsvps') {
              // Subcollection path: units/{unitId}/events/{eventId}/rsvps/{rsvpId}
              unitId = pathParts[1];
              eventId = pathParts[3];
              console.log('Found subcollection RSVP - unitId:', unitId, 'eventId:', eventId);
            } else if (pathParts.length === 2 && pathParts[0] === 'rsvps') {
              // Root-level path: rsvps/{rsvpId}
              // Use eventId from document data
              eventId = rsvpData.eventId;
              console.log('Found root-level RSVP - eventId from data:', eventId);
            } else {
              console.warn('Unexpected RSVP path structure:', rsvpDoc.ref.path);
              // Try to get eventId from document data as fallback
              eventId = rsvpData.eventId;
            }

            // If we still don't have eventId, try to get it from document data
            if (!eventId && rsvpData.eventId) {
              eventId = rsvpData.eventId;
            }

            let eventName = 'Unknown Event';
            let eventDate: Date | undefined = undefined;
            let unitName = 'Unknown Unit';
            let eventStatus: Event['eventStatus'] | undefined = undefined;
            
            try {
              if (unitId && eventId) {
                adminLog('Looking up event via subcollection path - unitId:', unitId, 'eventId:', eventId);
                // Try subcollection path first
                const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
                if (eventDoc.exists()) {
                  const eventData = eventDoc.data();
                  eventName = eventData.name;
                  eventDate = eventData.date?.toDate();
                  eventStatus = eventData.eventStatus;
                  adminLog('Event found via subcollection:', {
                    eventName,
                    eventDate: eventDate?.toISOString(),
                    eventStatus,
                  });
                } else {
                  adminWarn('Event document does not exist:', 'units', unitId, 'events', eventId);
                }

                const unitDoc = await getDoc(doc(db, 'units', unitId));
                if (unitDoc.exists()) {
                  unitName = unitDoc.data().name;
                  adminLog('Unit found:', unitName);
                } else {
                  adminWarn('Unit document does not exist:', unitId);
                }
              } else if (eventId) {
                adminLog('Searching for event across accessible units - eventId:', eventId, 'units to search:', units.length);
                // Search for event across user's accessible units
                // This handles root-level RSVPs that don't have unitId in the path
                let foundEvent = false;
                for (const unit of units) {
                  try {
                    adminLog('Checking unit:', unit.id, unit.name);
                    const eventDoc = await getDoc(doc(db, 'units', unit.id, 'events', eventId));
                    if (eventDoc.exists()) {
                      const eventData = eventDoc.data();
                      eventName = eventData.name;
                      eventDate = eventData.date?.toDate();
                      eventStatus = eventData.eventStatus;
                      unitId = unit.id;
                      unitName = unit.name;
                      foundEvent = true;
                      adminLog('Event found in unit:', unit.id, 'eventName:', eventName, 'eventDate:', eventDate?.toISOString());
                      console.log('Found event in unit:', unit.id, 'eventId:', eventId);
                      break;
                    } else {
                      adminLog('Event not found in unit:', unit.id);
                    }
                  } catch (err) {
                    adminError('Error checking unit for event:', unit.id, err);
                    // Continue searching other units
                    console.debug('Event not found in unit:', unit.id);
                  }
                }
                
                if (!foundEvent) {
                  adminWarn('Event not found in any accessible unit for eventId:', eventId);
                  console.warn('Event not found in any accessible unit for eventId:', eventId);
                }
              } else {
                adminWarn('No unitId or eventId available for RSVP:', rsvpDoc.id);
              }
            } catch (err) {
              adminError('Error fetching event/unit:', err, 'unitId:', unitId, 'eventId:', eventId);
              console.error('Error fetching event/unit:', err, 'unitId:', unitId, 'eventId:', eventId);
            }

            if (!eventName && typeof rsvpData.eventName === 'string' && rsvpData.eventName.trim().length > 0) {
              eventName = rsvpData.eventName;
              adminLog('Using eventName from RSVP data:', eventName);
            }

            if (!eventDate) {
              const rawEventDate = (rsvpData as any).eventDate;
              if (rawEventDate?.toDate) {
                eventDate = rawEventDate.toDate();
                adminLog('Parsed eventDate from Firestore timestamp:', eventDate?.toISOString());
              } else if (rawEventDate) {
                const parsedDate = new Date(rawEventDate);
                if (!Number.isNaN(parsedDate.getTime())) {
                  eventDate = parsedDate;
                  adminLog('Parsed eventDate from raw value:', eventDate?.toISOString());
                } else {
                  adminWarn('Failed to parse eventDate from raw value:', rawEventDate);
                }
              } else {
                adminWarn('No eventDate available in RSVP data');
              }
            }

            if (!eventStatus && rsvpData.eventStatus) {
              eventStatus = rsvpData.eventStatus;
              adminLog('Using eventStatus from RSVP data:', eventStatus);
            }

            if ((!unitName || unitName === 'Unknown Unit') && (rsvpData.unitName || rsvpData.unit)) {
              unitName = rsvpData.unitName || rsvpData.unit;
              adminLog('Using unitName from RSVP data:', unitName);
            }

            const processedRsvp = {
              id: rsvpDoc.id,
              eventId: eventId || rsvpData.eventId,
              ...rsvpData,
              eventName,
              eventDate,
              unitId,
              unitName,
              lodgeId: unitId, // Legacy support
              lodgeName: unitName, // Legacy support
              createdAt: rsvpData.createdAt?.toDate(),
              updatedAt: rsvpData.updatedAt?.toDate(),
              checkInTime: rsvpData.checkInTime?.toDate(),
              eventStatus,
            } as RSVP & { eventName?: string; eventDate?: Date; unitId?: string; unitName?: string; lodgeId?: string; lodgeName?: string; eventStatus?: EventStatus };
            
            adminLog('Processed RSVP result:', {
              id: processedRsvp.id,
              status: processedRsvp.status,
              eventId: processedRsvp.eventId,
              eventName: processedRsvp.eventName,
              eventDate: processedRsvp.eventDate?.toISOString(),
              unitId: processedRsvp.unitId,
              unitName: processedRsvp.unitName,
              eventStatus: processedRsvp.eventStatus,
              paid: processedRsvp.paid,
              apologies: processedRsvp.apologies,
            });
            
            return processedRsvp;
          })
        );

        const activeFetchedRsvps = fetchedRsvps.filter(
          (rsvp): rsvp is NonNullable<typeof rsvp> => rsvp != null,
        );

        console.log('Fetched RSVPs with details:', activeFetchedRsvps);
        adminLog('=== FETCHED RSVPs SUMMARY ===');
        adminLog('Total fetched RSVPs:', activeFetchedRsvps.length);
        adminLogTable('All Fetched RSVPs', activeFetchedRsvps.map(rsvp => ({
          id: rsvp.id,
          status: rsvp.status,
          eventId: rsvp.eventId,
          eventName: rsvp.eventName,
          eventDate: rsvp.eventDate?.toISOString() || 'N/A',
          unitId: rsvp.unitId || 'N/A',
          unitName: rsvp.unitName || 'N/A',
          eventStatus: rsvp.eventStatus || 'N/A',
          paid: rsvp.paid || false,
          apologies: rsvp.apologies || '',
          userId: rsvp.userId || 'N/A',
          email: rsvp.email || 'N/A',
        })));
        
        // Filter out RSVPs for deleted events (only if eventStatus is explicitly 'deleted')
        // If eventStatus is undefined, keep the RSVP (event might not be found but RSVP is still valid)
        const filteredRsvps = activeFetchedRsvps.filter((rsvp) => {
          // Only filter out if eventStatus is explicitly 'deleted'
          // Keep RSVPs with undefined eventStatus (event not found or unknown)
          const shouldKeep = rsvp.eventStatus !== 'deleted';
          if (!shouldKeep) {
            adminLog('Filtering out RSVP with deleted event:', rsvp.id, rsvp.eventName);
            console.log('Filtering out RSVP with deleted event:', rsvp.id, rsvp.eventName);
          }
          // Log RSVPs that might have issues
          if (!rsvp.status) {
            adminWarn('RSVP missing status field:', rsvp.id, rsvp.eventName, rsvp);
            console.warn('RSVP missing status field:', rsvp.id, rsvp.eventName, rsvp);
          }
          if (!rsvp.eventDate) {
            adminWarn('RSVP missing eventDate:', rsvp.id, rsvp.eventName, 'eventId:', rsvp.eventId, 'unitId:', rsvp.unitId);
            console.warn('RSVP missing eventDate:', rsvp.id, rsvp.eventName, 'eventId:', rsvp.eventId, 'unitId:', rsvp.unitId);
          }
          return shouldKeep;
        });
        console.log('Filtered RSVPs (after removing deleted events):', filteredRsvps.length);
        adminLog('Filtered RSVPs count:', filteredRsvps.length);
        adminLog('=== RSVP FETCH END ===');
        adminLogTable('Filtered RSVPs (Final)', filteredRsvps.map(rsvp => ({
          id: rsvp.id,
          status: rsvp.status,
          eventId: rsvp.eventId,
          eventName: rsvp.eventName,
          eventDate: rsvp.eventDate?.toISOString() || 'N/A',
          unitId: rsvp.unitId || 'N/A',
          unitName: rsvp.unitName || 'N/A',
        })));
        setMyRsvps(filteredRsvps);
      } catch (error) {
        console.error('Error fetching user RSVPs:', error);
      } finally {
        setRsvpsLoading(false);
      }
    };

    fetchMyRsvps();
  }, [currentUser, userProfile, units, unitsLoading]);

  // Check SumUp payment status for RSVPs with "Payment Started (SumUp)" status
  useEffect(() => {
    const checkSumUpPayments = async () => {
      if (!myRsvps.length) return;

      const sumupRsvps = myRsvps.filter(
        (rsvp) =>
          (rsvp as any).paymentMethod === 'sumup' &&
          (rsvp as any).paymentStatus === 'Payment Started' &&
          (rsvp as any).sumupCheckoutId &&
          rsvp.unitId &&
          rsvp.eventId
      );

      if (sumupRsvps.length === 0) return;

      const checkSumUpPaymentStatus = httpsCallable(functions, 'checkSumUpPaymentStatus');

      // Check each RSVP's payment status
      for (const rsvp of sumupRsvps) {
        try {
          const result = await checkSumUpPaymentStatus({
            unitId: rsvp.unitId!,
            eventId: rsvp.eventId,
            rsvpId: rsvp.id,
            checkoutId: (rsvp as any).sumupCheckoutId,
          });

          const data = result.data as {
            status: string;
            updatedStatus?: string;
            paid?: boolean;
          };

          // If payment status changed, update local state
          if (data.updatedStatus && data.updatedStatus !== (rsvp as any).paymentStatus) {
            setMyRsvps((prev) =>
              prev.map((r) =>
                r.id === rsvp.id
                  ? {
                      ...r,
                      paymentStatus: data.updatedStatus,
                      paid: data.paid || r.paid,
                    }
                  : r
              )
            );
          }
        } catch (error) {
          console.error('Error checking SumUp payment status:', error);
          // Silently fail - don't disrupt user experience
        }
      }
    };

    checkSumUpPayments();
  }, [myRsvps]);

  useEffect(() => {
    const checkSlickPayPayments = async () => {
      if (!myRsvps.length) return;

      const slickRsvps = myRsvps.filter(
        (rsvp) =>
          (rsvp as { paymentMethod?: string }).paymentMethod === 'slickpay' &&
          (rsvp as { paymentStatus?: string }).paymentStatus === 'Payment Started' &&
          (rsvp as { slickpayInvoiceId?: string }).slickpayInvoiceId &&
          rsvp.unitId &&
          rsvp.eventId
      );

      if (slickRsvps.length === 0) return;

      const checkSlickPayPaymentStatus = httpsCallable(functions, 'checkSlickPayPaymentStatus');

      for (const rsvp of slickRsvps) {
        try {
          const result = await checkSlickPayPaymentStatus({
            unitId: rsvp.unitId!,
            eventId: rsvp.eventId,
            rsvpId: rsvp.id,
            slickpayInvoiceId: (rsvp as { slickpayInvoiceId?: string }).slickpayInvoiceId,
          });

          const data = result.data as {
            status: string;
            updatedStatus?: string;
            paid?: boolean;
          };

          if (data.updatedStatus && data.updatedStatus !== (rsvp as { paymentStatus?: string }).paymentStatus) {
            setMyRsvps((prev) =>
              prev.map((r) =>
                r.id === rsvp.id
                  ? {
                      ...r,
                      paymentStatus: data.updatedStatus,
                      paid: data.paid || r.paid,
                    }
                  : r
              )
            );
          }
        } catch (error) {
          console.error('Error checking SlickPay payment status:', error);
        }
      }
    };

    void checkSlickPayPayments();
    const interval = setInterval(() => {
      void checkSlickPayPayments();
    }, 30_000);

    return () => clearInterval(interval);
  }, [myRsvps]);

  // Fetch user's event invitations
  useEffect(() => {
    const fetchEventInvitations = async () => {
      if (!currentUser) return;

      try {
        const invitationsQuery = query(
          collection(db, 'users', currentUser.uid, 'eventInvitations'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );

        const invitationsSnapshot = await getDocs(invitationsQuery);
        const fetchedInvitations: EventInvitation[] = invitationsSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            eventId: data.eventId,
            eventName: data.eventName,
            eventDate: data.eventDate?.toDate() || new Date(),
            eventLocation: data.eventLocation,
            unitId: data.unitId || data.lodgeId || '',
            unitName: data.unitName || data.lodgeName || '',
            lodgeId: data.lodgeId,
            lodgeName: data.lodgeName,
            invitedBy: data.invitedBy,
            invitedByName: data.invitedByName,
            inviterEmail: data.inviterEmail,
            message: data.message,
            invitationToken: data.invitationToken,
            inviterPaysFor: data.inviterPaysFor,
            status: data.status,
            createdAt: data.createdAt?.toDate() || new Date(),
            respondedAt: data.respondedAt?.toDate(),
          };
        });

        setEventInvitations(fetchedInvitations);
      } catch (error) {
        console.error('Error fetching event invitations:', error);
      }
    };

    fetchEventInvitations();
  }, [currentUser]);

  // Fetch user's outstanding invoices
  useEffect(() => {
    const fetchMyInvoices = async () => {
      if (!currentUser) return;

      try {
        // Fetch invoices with outstanding or partial status (excluding voided)
        const invoicesQuery = query(
          collectionGroup(db, 'invoices'),
          where('ownerUid', '==', currentUser.uid),
          where('status', 'in', ['outstanding', 'partial'])
        );

        const invoicesSnapshot = await getDocs(invoicesQuery);
        
        // Also fetch all user invoices to check for outstanding balances regardless of status
        const allInvoicesQuery = query(
          collectionGroup(db, 'invoices'),
          where('ownerUid', '==', currentUser.uid)
        );
        
        const allInvoicesSnapshot = await getDocs(allInvoicesQuery);
        
        // Combine and deduplicate
        const allInvoiceDocs = new Map();
        invoicesSnapshot.docs.forEach(doc => allInvoiceDocs.set(doc.id, doc));
        
        // Add invoices with outstanding balance even if status is 'paid' (but exclude voided)
        allInvoicesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.status !== 'voided' && data.outstandingBalance > 0 && !allInvoiceDocs.has(doc.id)) {
            allInvoiceDocs.set(doc.id, doc);
          }
        });
        
        const fetchedInvoices = await Promise.all(
          Array.from(allInvoiceDocs.values()).map(async (invoiceDoc) => {
            const invoiceData = invoiceDoc.data();
            if (invoiceData.deletedAt) {
              return null;
            }

            const pathParts = invoiceDoc.ref.path.split('/');
            const unitId = pathParts[1];
            const eventId = pathParts[3];

            let eventName = 'Unknown Event';
            try {
              const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
              if (eventDoc.exists()) {
                eventName = eventDoc.data().name;
              }
            } catch (err) {
              console.error('Error fetching event:', err);
            }

            return {
              id: invoiceDoc.id,
              ...invoiceData,
              eventName,
              createdAt: normaliseDateValue(invoiceData.createdAt),
              updatedAt: normaliseDateValue(invoiceData.updatedAt),
              paymentHistory: invoiceData.paymentHistory?.map((p: any) => ({
                ...p,
                date: normaliseDateValue(p.date),
              })),
              remindersSent: (invoiceData.remindersSent || [])
                .map((d: any) => normaliseDateValue(d))
                .filter((d: Date | undefined): d is Date => d instanceof Date),
            } as Invoice & { eventName?: string };
          })
        );

        setMyInvoices(
          fetchedInvoices.filter((inv): inv is Invoice & { eventName?: string } => inv !== null)
        );
      } catch (error) {
        console.error('Error fetching user invoices:', error);
      }
    };

    fetchMyInvoices();
  }, [currentUser]);

  useEffect(() => {
    let cancelled = false;
    const loadBreakdowns = async () => {
      const uniqueInvoices = new Map<string, Invoice>();
      for (const inv of myInvoices) {
        uniqueInvoices.set(inv.id, inv);
      }
      const entries = await Promise.all(
        Array.from(uniqueInvoices.values()).map(async (inv) => {
          if (!inv.unitId || !inv.eventId) {
            return null;
          }
          const ids = (inv.rsvps || []).filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
          if (ids.length === 0) {
            return null;
          }
          try {
            const eventSnap = await getDoc(doc(db, 'units', inv.unitId, 'events', inv.eventId));
            if (!eventSnap.exists() || cancelled) {
              return null;
            }
            const mappedEvent = mapEventData(eventSnap, inv.unitId);
            // Skip bookings this user can't read (e.g. a guest since moved to another invoice).
            const rsvpResults = await Promise.allSettled(
              ids.map((rid) =>
                getDoc(doc(db, 'units', inv.unitId!, 'events', inv.eventId!, 'rsvps', rid)),
              ),
            );
            const loaded: RSVP[] = [];
            for (const result of rsvpResults) {
              if (result.status !== 'fulfilled') continue;
              const s = result.value;
              if (!s.exists()) continue;
              const r = mapRsvpData(s);
              if (r) loaded.push(r);
            }
            if (loaded.length === 0 || cancelled) {
              return null;
            }
            return [inv.id, { event: mappedEvent, rsvps: loaded }] as const;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) {
        return;
      }
      const next = new Map<string, { event: Event; rsvps: RSVP[] }>();
      for (const row of entries) {
        if (row) {
          next.set(row[0], row[1]);
        }
      }
      setInvoiceBreakdowns(next);
    };
    void loadBreakdowns();
    return () => {
      cancelled = true;
    };
  }, [myInvoices]);

  // Keep invoice data in sync for RSVP-linked invoices so payment status/amount updates live.
  useEffect(() => {
    if (!currentUser || myRsvps.length === 0) {
      setRsvpInvoices(new Map());
      return;
    }

    const rsvpsWithInvoiceIds = myRsvps.filter(
      (rsvp) => rsvp.invoiceId && rsvp.unitId && rsvp.eventId
    );

    if (rsvpsWithInvoiceIds.length === 0) {
      setRsvpInvoices(new Map());
      return;
    }

    const invoiceMap = new Map<string, Invoice>();
    const unsubscribers = rsvpsWithInvoiceIds.map((rsvp) =>
      onSnapshot(
        doc(db, 'units', rsvp.unitId!, 'events', rsvp.eventId!, 'invoices', rsvp.invoiceId!),
        (invoiceSnap) => {
          if (!invoiceSnap.exists()) {
            invoiceMap.delete(rsvp.id);
            setRsvpInvoices(new Map(invoiceMap));
            return;
          }

          const invoiceData = invoiceSnap.data();
          if (invoiceData.deletedAt) {
            invoiceMap.delete(rsvp.id);
            setRsvpInvoices(new Map(invoiceMap));
            return;
          }

          const mappedInvoice = {
            id: invoiceSnap.id,
            ...invoiceData,
            createdAt: normaliseDateValue(invoiceData.createdAt),
            updatedAt: normaliseDateValue(invoiceData.updatedAt),
            paymentHistory: invoiceData.paymentHistory?.map((p: any) => ({
              ...p,
              date: normaliseDateValue(p.date),
            })) || [],
            remindersSent: (invoiceData.remindersSent || [])
              .map((d: any) => normaliseDateValue(d))
              .filter((d: Date | undefined): d is Date => d instanceof Date),
          } as Invoice;

          invoiceMap.set(rsvp.id, mappedInvoice);
          setRsvpInvoices(new Map(invoiceMap));

          // Keep the invoice collection used by totals/metrics aligned with live invoice updates.
          setMyInvoices((prev) => {
            const existing = prev.find((inv) => inv.id === mappedInvoice.id);
            if (!existing) {
              return [...prev, mappedInvoice];
            }

            return prev.map((inv) => (inv.id === mappedInvoice.id ? { ...existing, ...mappedInvoice } : inv));
          });
        },
        (error) => {
          console.error('Error listening to RSVP invoice:', error);
        }
      )
    );

    if (isAdmin) {
      adminLog(
        'Subscribed to RSVP invoice updates:',
        rsvpsWithInvoiceIds.map((rsvp) => ({ rsvpId: rsvp.id, invoiceId: rsvp.invoiceId }))
      );
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [currentUser, myRsvps]);

  useEffect(() => {
    const pairs = new Map<string, { unitId: string; eventId: string }>();
    for (const inv of myInvoices) {
      if (inv.unitId && inv.eventId) {
        pairs.set(`${inv.unitId}\t${inv.eventId}`, { unitId: inv.unitId, eventId: inv.eventId });
      }
    }
    if (pairs.size === 0) {
      return;
    }

    const unsubs = Array.from(pairs.values()).map(({ unitId, eventId }) =>
      onSnapshot(doc(db, 'units', unitId, 'events', eventId), (snap) => {
        if (!snap.exists()) return;
        const mapped = mapEventData(snap, unitId);
        setInvoiceBreakdowns((prev) => {
          let changed = false;
          const next = new Map(prev);
          for (const [invId, row] of next.entries()) {
            if (row.event.id === mapped.id && row.event.unitId === mapped.unitId) {
              next.set(invId, { event: mapped, rsvps: row.rsvps });
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }),
    );

    return () => unsubs.forEach((u) => u());
  }, [myInvoices]);

  // Fetch unread announcements
  useEffect(() => {
    const fetchUnreadAnnouncements = async () => {
      if (!currentUser) return;

      try {
        const announcementsQuery = query(collectionGroup(db, 'announcements'));
        const announcementsSnapshot = await getDocs(announcementsQuery);
        
        const unreadAnnouncementsList = await Promise.all(
          announcementsSnapshot.docs
            .filter((doc) => {
              const data = doc.data();
              const readBy = data.readBy || [];
              return !readBy.includes(currentUser.uid);
            })
            .map(async (announcementDoc) => {
              const announcementData = announcementDoc.data();
              
              const pathParts = announcementDoc.ref.path.split('/');
              const unitId = pathParts[1];
              const eventId = pathParts[3];

              let eventName = 'Unknown Event';
              let eventDate: Date | undefined;
              let eventStatus: EventStatus | undefined;
              let eventDocExists = false;
              try {
                const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
                if (eventDoc.exists()) {
                  eventDocExists = true;
                  const ev = eventDoc.data();
                  eventName = ev.name;
                  eventDate = ev.date?.toDate?.();
                  eventStatus = ev.eventStatus;
                }
              } catch (err) {
                console.error('Error fetching event:', err);
              }

              return {
                id: announcementDoc.id,
                ...announcementData,
                eventName,
                unitId,
                lodgeId: unitId, // Legacy support
                eventId,
                eventDate,
                eventStatus,
                eventDocExists,
                createdAt: announcementData.createdAt?.toDate(),
              } as DashboardAnnouncementRow;
            })
        );

        setUnreadAnnouncementCandidates(unreadAnnouncementsList);
      } catch (error) {
        console.error('Error fetching unread announcements:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUnreadAnnouncements();
  }, [currentUser]);

  // Fetch unit statistics
  useEffect(() => {
    const fetchUnitStats = async () => {
      if (!currentUser || units.length === 0) {
        setUnitsWithStats([]);
        setUnitsStatsLoading(false);
        return;
      }

      try {
        const unitStatsPromises = units.map(async (unit) => {
          // Get user's role in this unit
          const memberships = userProfile?.units || userProfile?.units;
          const userMembership = memberships?.find((l: any) => {
            const id = 'unitId' in l ? l.unitId : l.lodgeId;
            return id === unit.id;
          });
          const userRole = userMembership?.role || 'Member';

          // Count members
          const memberCount = unit.members.length;

          // Get events for this unit
          const eventsQuery = query(
            collection(db, 'units', unit.id, 'events'),
            orderBy('date', 'asc')
          );
          const eventsSnapshot = await getDocs(eventsQuery);
          const eventCount = eventsSnapshot.size;

          // Find next upcoming event
          const now = new Date();
          let nextEventDate: Date | null = null;
          
          for (const eventDoc of eventsSnapshot.docs) {
            const eventData = eventDoc.data();
            const eventDate = eventData.date?.toDate();
            if (eventDate && eventDate > now) {
              nextEventDate = eventDate;
              break; // Since we ordered by date, first future event is the next one
            }
          }

          // Get entity name based on unit type
          let entityName = 'Unit'; // Default
          if (unit.typeId && unitTypes.length > 0) {
            const unitType = unitTypes.find(ut => ut.id === unit.typeId);
            if (unitType) {
              entityName = unitType.entityName;
            }
          }

          return {
            unit,
            memberCount,
            eventCount,
            nextEventDate,
            userRole,
            entityName,
          } as UnitWithStats;
        });

        const stats = await Promise.all(unitStatsPromises);
        setUnitsWithStats(stats);
      } catch (error) {
        console.error('Error fetching unit stats:', error);
      } finally {
        setUnitsStatsLoading(false);
      }
    };

    fetchUnitStats();
  }, [units, currentUser, userProfile, unitTypes]);

  const handleAcceptInvitation = async (invitation: EventInvitation) => {
    try {
      const unitId = invitation.unitId || invitation.lodgeId;
      navigate(`/unit/${unitId}/event/${invitation.eventId}/invitation/${invitation.invitationToken}`);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      showError('Failed to accept invitation. Please try again.');
    }
  };

  const handleDeclineInvitation = async (invitation: EventInvitation) => {
    if (!currentUser) return;

    try {
      await updateDoc(
        doc(db, 'users', currentUser.uid, 'eventInvitations', invitation.id),
        {
          status: 'declined',
          respondedAt: new Date(),
        }
      );

      setEventInvitations(eventInvitations.filter((inv) => inv.id !== invitation.id));
      showSuccess('Invitation declined');
    } catch (error) {
      console.error('Error declining invitation:', error);
      showError('Failed to decline invitation. Please try again.');
    }
  };

  const handleSelectUnit = (unitId: string) => {
    const unit = units.find((l) => l.id === unitId);
    if (unit) {
      setCurrentUnit(unit);
      navigate(`/unit/${unitId}/dashboard`);
    }
  };

  const handleDismissApplication = async (applicationId: string) => {
    if (!currentUser) return;

    try {
      // Get current dismissed applications
      const dismissedIds = userProfile?.dismissedUnitApplications || userProfile?.dismissedLodgeApplications || [];
      
      // Add this application to the dismissed list
      const updatedDismissedIds = [...dismissedIds, applicationId];
      
      // Update Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), {
        dismissedUnitApplications: updatedDismissedIds,
      });

      // Update local state
      setPendingUnits(pendingUnits.filter(app => app.id !== applicationId));
      
      // Refresh user profile
      await refreshUserProfile();
      
      showSuccess('Application dismissed');
    } catch (error) {
      console.error('Error dismissing application:', error);
      showError('Failed to dismiss application. Please try again.');
    }
  };

  const handleDismissAnnouncement = async (announcement: DashboardAnnouncementRow) => {
    if (!currentUser) return;

    try {
      const unitId = announcement.unitId || announcement.lodgeId;
      const { eventId, id: announcementId } = announcement;

      if (!unitId || !eventId || !announcementId) {
        console.error('Missing announcement path information');
        showError('Unable to dismiss announcement');
        return;
      }

      const announcementRef = doc(db, 'units', unitId, 'events', eventId, 'announcements', announcementId);
      
      // Add current user to the readBy array
      await updateDoc(announcementRef, {
        readBy: arrayUnion(currentUser.uid),
      });

      // Update local state
      setUnreadAnnouncementCandidates((prev) => prev.filter((a) => a.id !== announcement.id));
      
      showSuccess('Announcement dismissed');
    } catch (error) {
      console.error('Error dismissing announcement:', error);
      showError('Failed to dismiss announcement. Please try again.');
    }
  };

  const getRsvpStatus = useCallback(getPersonalDashboardRsvpStatus, []);

  const getEventTiming = useCallback((
    rsvp: RSVP & { eventName?: string; eventDate?: Date; unitId?: string; unitName?: string; lodgeId?: string; lodgeName?: string; eventStatus?: EventStatus },
    referenceDate: Date
  ): 'upcoming' | 'past' => {
    if (rsvp.eventDate) {
      return rsvp.eventDate <= referenceDate ? 'past' : 'upcoming';
    }

    if (rsvp.eventStatus === 'completed' || rsvp.eventStatus === 'closed') {
      return 'past';
    }

    return 'upcoming';
  }, []);

  const handleEventAction = (item: UnifiedEventItem) => {
    switch (item.type) {
      case 'invitation':
        if (item.invitationData) {
          handleAcceptInvitation(item.invitationData);
        }
        break;
      case 'payment':
        if (item.invoiceId) {
          navigate(`/payment/${item.invoiceId}`);
        }
        break;
      case 'pending_rsvp':
      case 'waitlist':
      case 'upcoming':
      case 'past':
        if (item.unitId && item.eventId && item.rsvpId) {
          navigate(`/unit/${item.unitId}/event/${item.eventId}/rsvp/${item.rsvpId}/dashboard`);
        }
        break;
    }
  };

  const getActionButton = (item: UnifiedEventItem) => {
    switch (item.type) {
      case 'invitation':
        return (
          <div className="flex w-full flex-col gap-2 sm:inline-flex sm:w-auto sm:flex-row sm:flex-wrap">
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={(e) => {
                e.stopPropagation();
                handleEventAction(item);
              }}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Respond
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={(e) => {
                e.stopPropagation();
                if (item.invitationData) {
                  handleDeclineInvitation(item.invitationData);
                }
              }}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Decline
            </Button>
          </div>
        );
      case 'payment':
        return (
          <div className="flex w-full flex-col gap-2 sm:inline-flex sm:w-auto sm:flex-row sm:flex-wrap">
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={(e) => {
                e.stopPropagation();
                if (item.invoiceId) {
                  navigate(`/payment/${item.invoiceId}`);
                }
              }}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Pay Now
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={(e) => {
                e.stopPropagation();
                if (item.unitId && item.eventId && item.rsvpId) {
                  navigate(`/unit/${item.unitId}/event/${item.eventId}/rsvp/${item.rsvpId}/dashboard`);
                }
              }}
            >
              <Eye className="mr-2 h-4 w-4" />
              View RSVP
            </Button>
          </div>
        );
      case 'pending_rsvp':
        return (
          <Button
            size="sm"
            className="w-full sm:w-auto"
            onClick={(e) => {
              e.stopPropagation();
              handleEventAction(item);
            }}
          >
            <Clock className="mr-2 h-4 w-4" />
            View Status
          </Button>
        );
      case 'waitlist':
        return (
          <Button
            size="sm"
            className="w-full sm:w-auto"
            onClick={(e) => {
              e.stopPropagation();
              handleEventAction(item);
            }}
          >
            <Ticket className="mr-2 h-4 w-4" />
            View RSVP
          </Button>
        );
      case 'upcoming':
        return (
          <Button
            size="sm"
            className="w-full sm:w-auto"
            onClick={(e) => {
              e.stopPropagation();
              handleEventAction(item);
            }}
          >
            <Eye className="mr-2 h-4 w-4" />
            View RSVP
          </Button>
        );
      case 'past':
        return (
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={(e) => {
              e.stopPropagation();
              handleEventAction(item);
            }}
          >
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </Button>
        );
      default:
        return null;
    }
  };

  // Calculate metrics
  // Include RSVPs with 'confirmed' status, or RSVPs without a status field (legacy support)
  // Also include RSVPs that aren't declined or cancelled
  adminLog('=== METRICS CALCULATION START ===');
  adminLog('Total myRsvps:', myRsvps.length);
  adminLogTable('All myRsvps', myRsvps.map(rsvp => ({
    id: rsvp.id,
    status: rsvp.status || 'N/A',
    eventName: rsvp.eventName || 'N/A',
    eventDate: rsvp.eventDate?.toISOString() || 'N/A',
    unitId: rsvp.unitId || 'N/A',
    paid: rsvp.paid || false,
  })));
  
  const confirmedRsvps = myRsvps.filter((rsvp) => {
    const status = rsvp.status;
    // Exclude RSVPs with apologies or declined/cancelled status
    if (rsvp.apologies === true || status === 'declined' || status === 'cancelled') return false;
    // Include confirmed RSVPs
    if (status === 'confirmed') return true;
    // Include RSVPs without status (legacy support - treat as confirmed if they have eventDate)
    if (!status && rsvp.eventDate) return true;
    // For other statuses, only include if they have an eventDate (likely valid RSVPs)
    return !!rsvp.eventDate;
  });
  adminLog('Confirmed RSVPs count:', confirmedRsvps.length);
  adminLogTable('Confirmed RSVPs', confirmedRsvps.map(rsvp => ({
    id: rsvp.id,
    status: rsvp.status,
    eventName: rsvp.eventName,
    eventDate: rsvp.eventDate?.toISOString(),
  })));
  
  const pendingRsvps = myRsvps.filter((rsvp) => rsvp.status === 'pending' || (rsvp.status === 'waitlist' && rsvp.eventStatus === 'published'));
  adminLog('Pending RSVPs count:', pendingRsvps.length);
  adminLogTable('Pending RSVPs', pendingRsvps.map(rsvp => ({
    id: rsvp.id,
    status: rsvp.status,
    eventName: rsvp.eventName,
    eventDate: rsvp.eventDate?.toISOString(),
  })));
  
  const waitlistRsvps = myRsvps.filter((rsvp) => rsvp.status === 'waitlist' && rsvp.eventStatus !== 'published');
  adminLog('Waitlist RSVPs count:', waitlistRsvps.length);
  adminLogTable('Waitlist RSVPs', waitlistRsvps.map(rsvp => ({
    id: rsvp.id,
    status: rsvp.status,
    eventName: rsvp.eventName,
    eventDate: rsvp.eventDate?.toISOString(),
  })));
  
  const referenceDate = new Date();
  const upcomingEvents = confirmedRsvps.filter((rsvp) => getEventTiming(rsvp, referenceDate) === 'upcoming');
  adminLog('Upcoming events count:', upcomingEvents.length);
  adminLogTable('Upcoming Events', upcomingEvents.map(rsvp => ({
    id: rsvp.id,
    eventName: rsvp.eventName,
    eventDate: rsvp.eventDate?.toISOString(),
    unitName: rsvp.unitName,
  })));
  
  const pastEvents = confirmedRsvps.filter((rsvp) => getEventTiming(rsvp, referenceDate) === 'past');
  adminLog('Past events count:', pastEvents.length);
  adminLogTable('Past Events', pastEvents.map(rsvp => ({
    id: rsvp.id,
    eventName: rsvp.eventName,
    eventDate: rsvp.eventDate?.toISOString(),
    unitName: rsvp.unitName,
  })));
  
  adminLog('Total invoices:', myInvoices.length);
  adminLogTable('My Invoices', myInvoices.map(inv => ({
    id: inv.id,
    eventName: inv.eventName,
    status: inv.status,
    outstandingBalance: inv.outstandingBalance,
    totalAmount: inv.totalAmount,
  })));
  adminLog('=== METRICS CALCULATION END ===');

  // Create unified event list
  const unifiedEvents = useMemo((): UnifiedEventItem[] => {
    adminLog('=== UNIFIED EVENTS CALCULATION START ===');
    adminLog('Event invitations:', eventInvitations.length);
    adminLogTable('Event Invitations', eventInvitations.map(inv => ({
      id: inv.id,
      eventName: inv.eventName,
      eventDate: inv.eventDate?.toISOString(),
      unitName: inv.unitName,
      status: inv.status,
    })));
    adminLog('Pending RSVPs:', pendingRsvps.length);
    adminLog('Waitlist RSVPs:', waitlistRsvps.length);
    adminLog('Upcoming events:', upcomingEvents.length);
    adminLog('Past events:', pastEvents.length);
    adminLog('Total myRsvps:', myRsvps.length);
    
    const items: UnifiedEventItem[] = [];
    const now = new Date();

    // Add event invitations
    eventInvitations.forEach((invitation) => {
      adminLog('Adding invitation:', invitation.id, invitation.eventName);
      items.push({
        id: `invitation-${invitation.id}`,
        type: 'invitation',
        eventName: invitation.eventName,
        eventDate: invitation.eventDate,
        unitName: invitation.unitName || invitation.lodgeName || '',
        lodgeName: invitation.lodgeName || invitation.unitName || '',
        status: 'New Invitation',
        statusVariant: 'secondary',
        unitId: invitation.unitId,
        eventId: invitation.eventId,
        invitationData: invitation,
        originalData: invitation,
      });
    });

    // Process all RSVPs (regardless of status) with unified status logic
    const allRsvps = myRsvps;
    adminLog('Processing all RSVPs for unified events:', allRsvps.length);

    // Log all RSVPs being processed as a table
    adminLogTable('RSVPs Being Processed for Unified Events', allRsvps.map(rsvp => ({
      id: rsvp.id,
      status: rsvp.status,
      eventName: rsvp.eventName,
      eventDate: rsvp.eventDate?.toISOString() || 'N/A',
      eventId: rsvp.eventId || 'N/A',
      unitId: rsvp.unitId || 'N/A',
      unitName: rsvp.unitName || 'N/A',
      paid: rsvp.paid || false,
    })));
    
    allRsvps.forEach((rsvp, index) => {
      // Cancelled/declined bookings are no longer active RSVPs and should not appear in "My RSVP's".
      if (rsvp.status === 'cancelled' || rsvp.status === 'declined') {
        return;
      }

      const effectiveEventDate = rsvp.eventDate ?? null;

      // Find associated invoice - check multiple sources
      let invoice: Invoice | undefined;
      
      // First, check rsvpInvoices map (directly fetched from RSVP invoiceIds)
      invoice = rsvpInvoices.get(rsvp.id);
      if (invoice) {
        adminLog('Invoice found in rsvpInvoices map:', invoice.id);
      }
      
      // If not found, check myInvoices by RSVP ID in invoice's rsvps array
      if (!invoice) {
        invoice = myInvoices.find((inv) => Array.isArray(inv.rsvps) && inv.rsvps.includes(rsvp.id));
        if (invoice) {
          adminLog('Invoice found by RSVP ID in invoice rsvps array:', invoice.id);
        }
      }
      
      // If still not found and RSVP has invoiceId, try to find it by ID in myInvoices
      if (!invoice && rsvp.invoiceId) {
        invoice = myInvoices.find((inv) => inv.id === rsvp.invoiceId);
        if (invoice) {
          adminLog('Invoice found by invoiceId:', invoice.id);
        } else {
          adminWarn('RSVP has invoiceId but invoice not found in myInvoices:', rsvp.invoiceId);
        }
      }
      
      // Get status using the helper function
      const statusInfo = getRsvpStatus(
        rsvp,
        effectiveEventDate,
        invoice,
        invoice ? invoiceBreakdowns.get(invoice.id) : undefined,
      );
      const timing = getEventTiming(rsvp, now);
      
      // Check if RSVP has apologies - these should not show payment requirements
      // (declined/cancelled RSVPs are filtered out above, before reaching this point)
      const hasApologies = rsvp.apologies === true;
      const inviterCoversGuestRow = isInviteeCoveredByInviter(rsvp);
      const ticketDueForRow =
        invoice && invoice.status !== 'voided'
          ? getHeadlinePayableDueForDashboard(invoice, invoiceBreakdowns.get(invoice.id) ?? null)
          : 0;
      const isPendingVerification = invoice?.status === 'pending_verification';

      // Determine item type based on status
      let itemType: UnifiedEventItem['type'] = 'upcoming';
      if (rsvp.status === 'waitlist') {
        itemType = statusInfo.status === 'Awaiting Meal Choice' ? 'pending_rsvp' : 'waitlist';
      } else if (statusInfo.status === 'Pending Payment' || statusInfo.status === 'Payment Required') {
        itemType = 'payment';
      } else if (timing === 'past') {
        itemType = 'past';
      } else if (rsvp.status === 'pending') {
        itemType = 'pending_rsvp';
      }
      
      const unifiedItem = {
        id: `${itemType}-${rsvp.id}`,
        type: itemType,
        eventName: rsvp.eventName || 'Unknown Event',
        eventDate: effectiveEventDate,
        unitName: rsvp.unitName,
        lodgeName: rsvp.lodgeName || rsvp.unitName || 'Unknown Unit',
        status: statusInfo.status,
        statusVariant: statusInfo.variant,
        isPaid: isRsvpEffectivelyPaidForDashboard(
          rsvp,
          invoice,
          invoice ? invoiceBreakdowns.get(invoice.id) : undefined,
        ),
        // Match My RSVP: ticket subtotal minus paid (excludes txn fee from headline); host-paid invitees owe nothing
        amountDue: hasApologies
          ? undefined
          : inviterCoversGuestRow
            ? undefined
            : isPendingVerification
              ? undefined
            : invoice && invoice.status !== 'voided' && ticketDueForRow > INVOICE_AMOUNT_EPS
              ? ticketDueForRow
              : undefined,
        unitId: rsvp.unitId,
        lodgeId: rsvp.lodgeId,
        eventId: rsvp.eventId,
        rsvpId: rsvp.id,
        invoiceId: invoice?.id,
        hostPays: inviterCoversGuestRow,
        originalData: rsvp,
      };
      
      items.push(unifiedItem);
    });

    // Sort by event date descending (newest first), with a consistent type priority as a tiebreaker
    const typePriority: Record<UnifiedEventItem['type'], number> = {
      invitation: 0,
      payment: 1,
      pending_rsvp: 2,
      waitlist: 3,
      upcoming: 4,
      past: 5,
    };

    items.sort((a, b) => {
      const timeA = a.eventDate ? a.eventDate.getTime() : 0;
      const timeB = b.eventDate ? b.eventDate.getTime() : 0;

      if (timeA !== timeB) {
        // Newest first
        return timeB - timeA;
      }

      const priorityA = typePriority[a.type] ?? 99;
      const priorityB = typePriority[b.type] ?? 99;

      return priorityA - priorityB;
    });
    
    adminLog('=== UNIFIED EVENTS CALCULATION END ===');
    adminLog('Total unified events:', items.length);
    adminLog('Unified events breakdown:', {
      invitations: items.filter(i => i.type === 'invitation').length,
      pending_rsvp: items.filter(i => i.type === 'pending_rsvp').length,
      waitlist: items.filter(i => i.type === 'waitlist').length,
      payment: items.filter(i => i.type === 'payment').length,
      upcoming: items.filter(i => i.type === 'upcoming').length,
      past: items.filter(i => i.type === 'past').length,
    });
    adminLogTable('Final Unified Events List', items.map(item => ({
      id: item.id,
      type: item.type,
      eventName: item.eventName,
      eventDate: item.eventDate?.toISOString() || 'N/A',
      status: item.status,
      unitId: item.unitId || 'N/A',
      unitName: item.unitName || item.lodgeName || 'N/A',
      eventId: item.eventId || 'N/A',
      rsvpId: item.rsvpId || 'N/A',
      amountDue: item.amountDue || 0,
      isPaid: item.isPaid || false,
    })));
    
    return items;
  }, [myRsvps, eventInvitations, myInvoices, pendingRsvps, waitlistRsvps, upcomingEvents, pastEvents, getRsvpStatus, getEventTiming, rsvpInvoices, isAdmin, invoiceBreakdowns]);

  // Derive "Payments Due" from the same rows shown in "My RSVP's" so the tile always matches the list.
  // Keyed by invoice so two bookings on one shared invoice are not double counted.
  const { totalOwed, pendingPayments } = useMemo(() => {
    const owedByInvoice = new Map<string, number>();
    for (const item of unifiedEvents) {
      if (item.amountDue !== undefined && item.amountDue > INVOICE_AMOUNT_EPS) {
        owedByInvoice.set(item.invoiceId ?? item.id, item.amountDue);
      }
    }
    let sum = 0;
    owedByInvoice.forEach((amount) => {
      sum += amount;
    });
    return { totalOwed: sum, pendingPayments: owedByInvoice.size };
  }, [unifiedEvents]);

  const filteredUnifiedEvents = useMemo(() => {
    const now = new Date();

    return unifiedEvents.filter((item) => {
      if (rsvpFilter === 'ALL') {
        return true;
      }

      const isPast = item.eventDate ? item.eventDate < now : false;
      const hasOutstanding = item.amountDue !== undefined && item.amountDue > 0;
      const isFinished = isPast && !hasOutstanding;

      if (rsvpFilter === 'FINISHED') {
        return isFinished;
      }

      // ACTIVE
      return !isFinished;
    });
  }, [unifiedEvents, rsvpFilter]);

  if (unitsLoading || loading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading your personal dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show full dashboard even without units

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header />
      <WelcomeModal 
        open={showWelcome} 
        onClose={handleCloseWelcome}
        userName={userProfile?.firstName && userProfile?.lastName 
          ? `${userProfile.firstName} ${userProfile.lastName}` 
          : userProfile?.name || 'User'}
      />
      <main className="flex-1 w-full min-w-0 py-6 sm:py-8">
        <DashboardContentContainer>
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl font-bold leading-tight sm:text-3xl">
              Welcome back, {userProfile?.firstName && userProfile?.lastName 
                ? `${userProfile.firstName} ${userProfile.lastName}` 
                : userProfile?.name || 'User'}!
            </h1>
            <p className="mt-1 text-muted-foreground">Your personal dashboard</p>
          </div>

          {/* Personal Metrics */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:mb-8 sm:gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">My Memberships</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(userProfile?.units || []).length}</div>
                <p className="text-xs text-muted-foreground">
                  {(userProfile?.units || []).length === 1 ? 'Active membership' : 'Active memberships'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Attending</CardTitle>
                <Ticket className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{upcomingEvents.length}</div>
                <p className="text-xs text-muted-foreground">
                  {pastEvents.length} past events
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending RSVPs</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingRsvps.length + waitlistRsvps.length}</div>
                <p className="text-xs text-muted-foreground">
                  {waitlistRsvps.length} on waitlist
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Payments Due</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">£{totalOwed.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                  {pendingPayments} {pendingPayments === 1 ? 'payment' : 'payments'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Notifications</CardTitle>
                <Bell className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{visibleUnreadAnnouncements.length}</div>
                <p className="text-xs text-muted-foreground">
                  {eventInvitations.length} new {eventInvitations.length === 1 ? 'invite' : 'invites'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Unified Events & Actions Table */}
          <div className="mb-8">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-semibold sm:text-2xl">My RSVP's</h2>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Filter RSVPs">
                <Button
                  size="sm"
                  variant={rsvpFilter === 'ACTIVE' ? 'default' : 'outline'}
                  onClick={() => setRsvpFilter('ACTIVE')}
                  className="flex-1 min-[360px]:flex-none"
                >
                  Active
                </Button>
                <Button
                  size="sm"
                  variant={rsvpFilter === 'FINISHED' ? 'default' : 'outline'}
                  onClick={() => setRsvpFilter('FINISHED')}
                  className="flex-1 min-[360px]:flex-none"
                >
                  Finished
                </Button>
                <Button
                  size="sm"
                  variant={rsvpFilter === 'ALL' ? 'default' : 'outline'}
                  onClick={() => setRsvpFilter('ALL')}
                  className="flex-1 min-[360px]:flex-none"
                >
                  All
                </Button>
              </div>
            </div>
            {loading || rsvpsLoading ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
                  <p className="text-muted-foreground">Loading your events and actions...</p>
                </CardContent>
              </Card>
            ) : filteredUnifiedEvents.length > 0 ? (
              <>
                <div className="space-y-3 md:hidden">
                  {filteredUnifiedEvents.map((item) => (
                    <Card
                      key={item.id}
                      className="cursor-pointer transition-colors hover:bg-muted/40"
                      onClick={() => handleEventAction(item)}
                    >
                      <CardContent className="space-y-3 p-4">
                        <div className="min-w-0">
                          <p className="font-medium leading-snug break-words">{item.eventName}</p>
                          <p className="mt-1 text-sm text-muted-foreground">{formatDate(item.eventDate)}</p>
                          <p className="mt-0.5 text-sm text-muted-foreground break-words">
                            {item.unitName || item.lodgeName}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Badge variant={item.statusVariant} className="max-w-full whitespace-normal text-left">
                            {item.status}
                          </Badge>
                          <div className="text-right text-sm">
                            {item.hostPays ? (
                              <span className="text-muted-foreground">Host Pays</span>
                            ) : item.amountDue !== undefined ? (
                              <span className="font-semibold text-red-600">
                                £{item.amountDue.toFixed(2)}
                              </span>
                            ) : item.isPaid ? (
                              <span className="text-green-600">Paid</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </div>
                        <div className="border-t pt-3" onClick={(e) => e.stopPropagation()}>
                          {getActionButton(item)}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="hidden md:block">
                  <CardContent className="overflow-x-auto pt-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Event</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUnifiedEvents.map((item) => (
                          <TableRow
                            key={item.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleEventAction(item)}
                          >
                            <TableCell className="max-w-[200px] font-medium">
                              <span className="line-clamp-2">{item.eventName}</span>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{formatDate(item.eventDate)}</TableCell>
                            <TableCell className="max-w-[160px]">
                              <span className="line-clamp-2">{item.unitName || item.lodgeName}</span>
                            </TableCell>
                            <TableCell>
                              <Badge variant={item.statusVariant}>
                                {item.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {item.hostPays ? (
                                <span className="text-muted-foreground text-sm">Host Pays</span>
                              ) : item.amountDue !== undefined ? (
                                <span className="font-semibold text-red-600">
                                  £{item.amountDue.toFixed(2)}
                                </span>
                              ) : item.isPaid ? (
                                <span className="text-green-600 text-sm">Paid</span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              {getActionButton(item)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Events Yet</h3>
                  <p className="text-muted-foreground">
                    You don't have any upcoming events or pending actions.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Unread Announcements */}
          {visibleUnreadAnnouncements.length > 0 && (
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-semibold sm:text-2xl">Unread Announcements</h2>
              <div className="grid gap-4">
                {visibleUnreadAnnouncements.slice(0, 5).map((announcement) => (
                  <Card key={announcement.id} className="border-l-4 border-l-yellow-500">
                    <CardHeader>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-base break-words">{announcement.eventName}</CardTitle>
                          <CardDescription className="mt-1">{formatDate(announcement.createdAt)}</CardDescription>
                        </div>
                        <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-end">
                          <Badge variant="secondary">New</Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDismissAnnouncement(announcement)}
                            title="Dismiss announcement"
                            className="shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{announcement.message}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Pending Unit Applications */}
          {pendingUnits.length > 0 && (
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-semibold sm:text-2xl">Applications Under Review</h2>
              <div className="grid gap-4">
                {pendingUnits.map((application: PendingUnitApplication) => (
                  <Card 
                    key={application.id} 
                    className={`border-l-4 ${
                      application.reviewStatus === 'denied' 
                        ? 'border-l-red-500' 
                        : 'border-l-yellow-500'
                    }`}
                  >
                    <CardHeader>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <CardTitle className="text-base break-words">
                            {application.name}{application.unitNumber && unitDisplaysNumber(application.typeId, unitTypes) ? ` ${application.unitNumber}` : ''}
                          </CardTitle>
                          <CardDescription>
                            Submitted {application.submittedAt.toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </CardDescription>
                        </div>
                        <Badge 
                          className="w-fit shrink-0"
                          variant={application.reviewStatus === 'denied' ? 'destructive' : 'warning'}
                        >
                          {application.reviewStatus === 'denied' ? 'Denied' : 'Under Review'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {application.reviewStatus === 'pending' ? (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600">
                            Your unit application is currently being reviewed by our administrators. 
                            You'll receive an email notification once it has been processed.
                          </p>
                          {application.messageCount !== undefined && application.messageCount > 0 && (
                            <div className="flex items-center gap-2 text-sm text-blue-600">
                              <Mail className="h-4 w-4" />
                              <span className="font-medium">{application.messageCount} message{application.messageCount !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="bg-red-50 p-3 rounded-lg">
                            <p className="text-sm font-semibold text-red-900 mb-1">Reason for Denial:</p>
                            <p className="text-sm text-red-700">{application.denialReason}</p>
                          </div>
                          <p className="text-sm text-gray-600">
                            If you have questions about this decision, please contact our support team.
                          </p>
                          <div className="pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDismissApplication(application.id)}
                            >
                              <XCircle className="mr-2 h-4 w-4" />
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* My Memberships */}
          <div className="mb-8">
            <h2 className="mb-4 text-xl font-semibold sm:text-2xl">My Memberships</h2>
            {unitsLoading || unitsStatsLoading ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
                  <p className="text-muted-foreground">Loading your memberships...</p>
                </CardContent>
              </Card>
            ) : unitsWithStats.length > 0 ? (
              <>
                <div className="space-y-3 md:hidden">
                  {unitsWithStats.map((unitStat: UnitWithStats) => (
                    <Card key={unitStat.unit.id}>
                      <CardContent className="space-y-4 p-4">
                        <div className="min-w-0">
                          <p className="font-medium leading-snug break-words">
                            {unitStat.unit.name}
                            {(unitStat.unit.unitNumber || unitStat.unit.lodgeNumber) && unitDisplaysNumber(unitStat.unit.typeId, unitTypes) && ` #${unitStat.unit.unitNumber || unitStat.unit.lodgeNumber}`}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="outline">{unitStat.entityName}</Badge>
                            <Badge variant="secondary">{unitStat.userRole}</Badge>
                          </div>
                        </div>
                        <dl className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <dt className="text-muted-foreground">Members</dt>
                            <dd className="font-medium">{unitStat.memberCount}</dd>
                          </div>
                          <div>
                            <dt className="text-muted-foreground">Events</dt>
                            <dd className="font-medium">{unitStat.eventCount}</dd>
                          </div>
                          <div className="col-span-2">
                            <dt className="text-muted-foreground">Next event</dt>
                            <dd className="font-medium">
                              {unitStat.nextEventDate ? (
                                formatDate(unitStat.nextEventDate)
                              ) : (
                                <span className="text-muted-foreground font-normal">No upcoming events</span>
                              )}
                            </dd>
                          </div>
                        </dl>
                        <Button
                          className="w-full"
                          size="sm"
                          onClick={() => handleSelectUnit(unitStat.unit.id)}
                        >
                          View Dashboard
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col gap-2 p-4">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => navigate('/unit/join')}
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Join Unit
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => navigate('/unit/create')}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Create New Unit
                      </Button>
                    </CardContent>
                  </Card>
                </div>
                <Card className="hidden md:block">
                  <CardContent className="overflow-x-auto pt-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name and Number</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>My Role</TableHead>
                          <TableHead>Members</TableHead>
                          <TableHead>Events</TableHead>
                          <TableHead>Next Event</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unitsWithStats.map((unitStat: UnitWithStats) => (
                          <TableRow key={unitStat.unit.id}>
                            <TableCell className="max-w-[220px] font-medium">
                              <span className="line-clamp-2">
                                {unitStat.unit.name}
                            {(unitStat.unit.unitNumber || unitStat.unit.lodgeNumber) && unitDisplaysNumber(unitStat.unit.typeId, unitTypes) && ` #${unitStat.unit.unitNumber || unitStat.unit.lodgeNumber}`}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{unitStat.entityName}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{unitStat.userRole}</Badge>
                            </TableCell>
                            <TableCell>{unitStat.memberCount}</TableCell>
                            <TableCell>{unitStat.eventCount}</TableCell>
                            <TableCell className="whitespace-nowrap">
                              {unitStat.nextEventDate ? (
                                <span className="text-sm">
                                  {formatDate(unitStat.nextEventDate)}
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">No upcoming events</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                onClick={() => handleSelectUnit(unitStat.unit.id)}
                              >
                                View Dashboard
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50">
                          <TableCell colSpan={7} className="text-center">
                            <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
                              <Button
                                variant="outline"
                                onClick={() => navigate('/unit/join')}
                              >
                                <UserPlus className="mr-2 h-4 w-4" />
                                Join Unit
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => navigate('/unit/create')}
                              >
                                <Plus className="mr-2 h-4 w-4" />
                                Create New Unit
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Nothing to display!</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto px-1">
                    You haven't joined any lodges or chapters yet. Search for your lodge to request membership, create a new application, or wait for an invitation.
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                    <Button className="w-full sm:w-auto" onClick={() => navigate('/unit/join')}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Join a Lodge or Chapter
                    </Button>
                    <Button className="w-full sm:w-auto" variant="outline" onClick={() => navigate('/unit/create')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create New Unit
                    </Button>
                    <Button className="w-full sm:w-auto" variant="outline" onClick={() => navigate('/settings')}>
                      <UserCircle className="mr-2 h-4 w-4" />
                      Update Profile
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DashboardContentContainer>
      </main>
    </div>
  );
}

